import React, { Fragment, useEffect, useState, cloneElement, isValidElement } from 'react';
import { MultiGrid } from 'react-virtualized';
import classNames from 'classnames';
import { Tooltip, ClickAwayListener } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Parser } from 'expr-eval';

import layoutStyles from '../layout.module.css';
import tableStyles from './table.module.css';
import { parseHTML } from '../utils/excelService';

import Column from './utils/Column';
import { AWARD_OPTIMIZED_AT, awardValueColumn } from './utils/award';
import { checkIfAwardIsBlocked } from './utils/supplier';

const arithmeticParser = new Parser();


const STYLE = {
    border: '1px solid #ddd',
};
  
const STYLE_BOTTOM_LEFT_GRID = {
    borderRight: '2px solid #aaa',
    backgroundColor: '#f7f7f7',
};

const STYLE_TOP_LEFT_GRID = {
    borderBottom: '2px solid #aaa',
    borderRight: '2px solid #aaa',
    fontWeight: 'bold',
};

const STYLE_TOP_RIGHT_GRID = {
    borderBottom: '2px solid #aaa',
    fontWeight: 'bold',
};

// Formula tooltip component
const FormulaTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgb(0, 0, 0)',
    boxShadow: theme.shadows[1],
    fontSize: 16,
    fontWeight: 500,
    fontFamily: 'Selawik',
    paddingLeft: 10,
    paddingRight: 10,
    maxWidth: 'none',
  },
}))(Tooltip);

const isBaselineOrExtendedUcid = (columnUcid, extendedColumnId) => (
  columnUcid === 'baseline_price'
  || columnUcid === 'best_bid'
  || columnUcid === 'extended_price'
  || columnUcid === extendedColumnId
);

const getPercentBaselineExtendedJustify = (field, columnUcid, defaultJustify, extendedColumnId) => (
  field?.input_field_type === 'Percentage'
  && isBaselineOrExtendedUcid(columnUcid, extendedColumnId)
    ? 'flex-end'
    : defaultJustify
);

const isPercentInBaselineOrExtended = (field, columnUcid, extendedColumnId) => (
  field?.input_field_type === 'Percentage'
  && isBaselineOrExtendedUcid(columnUcid, extendedColumnId)
);

const isLotFieldTextType = (field) => field?.input_field_type === 'Text';

const isLotFieldPriceType = (field) => field?.input_field_type === 'Price';

const PercentValueWithSuffix = ({ value }) => {
  if (value === '') {
    return null;
  }
  return (
    <span className={tableStyles.percentValueGroup}>
      <span className={tableStyles.cellContent}>{value}</span>
      <span className={tableStyles.suffixTight}>%</span>
    </span>
  );
};

const Formula = ({ formula }) => {
  const [open, setOpen] = useState(false);
  const handleTooltipClose = () => setOpen(false);
  const handleTooltipOpen = () => setOpen(true);
  const tooltipTitle = formula && String(formula).trim()
    ? formula
    : 'Formula not specified';

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <FormulaTooltip
        PopperProps={{
          disablePortal: true,
        }}
        placement="top"
        onClose={handleTooltipClose}
        open={open}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        title={tooltipTitle}
        className={tableStyles.formulaTooltip}
      >
        <span
          className={classNames(
            tableStyles.suffix,
            tableStyles.link
          )}
          onClick={handleTooltipOpen}
        >
          (f)
        </span>
      </FormulaTooltip>
    </ClickAwayListener>
  );
};

function InnerContent({
    _headerRenderer,
    awardBy,
    awardColumnStyle,
    checkBestBid,
    checkLotFieldRanking,
    checkLTPRanking,
    checkTRPRanking,
    checkLSTRanking,
    columnCount,
    columnWidth,
    currencySymbol,
    data,
    excludedSet,
    getColumnValue,
    getColumnInstance,
    height,
    ignoredSet,
    isAwardDraft,
    isDummy,
    isTransformationAppliedAtLineItemLevel,
    lineItems,
    lineErrorSet,
    measure,
    optimizedAt,
    rowHeight,
    setContentRef,
    setShares,
    showLineItemsWithNoResponse,
    responsesCountByRow,
    responseNeededForAllLineItems,
    participatingSuppliersMap,
    blockedConfig,
    isColumnEditable,
    handleBaselinePayload,
    handleFormatBaseline,
    handleBaselinePaste,
    baselineColumnStyle,
    hiddenColumns,
    lotLevelFields,
    lotSubTotal,
    lotTotalPrice,
    transformationPrice,
    transformedBidColumns,
    transformationCostsData,
    enableLotTransformation,
    hasLotFields,
    totals_row,
    baselineTotal,
    bestBids,
    bestBidColumnId,
    eventSavingsTotal,
    bestBidOfLot,
    awardSavingTotal,
    award,
    getSupplierTotals,
    getRankWiseClassForLot,
    buyerColumns,
    suppliersOrder,
    suppliers,
    isLotExcluded,
    getLotShare,
    changeLotShare,
    isLotIgnored,
    isLotFullyGreyedOut,
    bidAction,
    eventInfo,
    buyer_totals,
    hiddenColumnSet: hiddenColumnSetProp,
    viewAwardScenario,
    ...props
  }) {
    
    const hiddenColumnSet = hiddenColumnSetProp || new Set(hiddenColumns);
    const formatPriceValue = (value, decimals = 2) => {
      if (value === null || value === undefined || value === '' || value === '-') {
        return value;
      }
      const parsedValue = Number(String(value).replace(/,/g, ''));
      if (Number.isNaN(parsedValue)) {
        return value;
      }
      return parsedValue.toLocaleString('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    };

    const parseNumericCell = (value) => {
      if (value === null || value === undefined || value === '' || value === '-') {
        return NaN;
      }
      const n = Number(String(value).replace(/,/g, ''));
      return Number.isNaN(n) ? NaN : n;
    };

    // In forward-style events (forward auction, sourcing sell, decreasing dutch) a higher
    // bid is better, so event savings is (bid - baseline). Otherwise it is (baseline - bid).
    const isHigherBidBetter = (() => {
      const info = eventInfo || {};
      const isSourcingSell = info.event_type === 'sourcing' && info.sourcing_type === 'sell';
      const isForwardAuction = info.event_type === 'auction' && info.auction_type === 'b';
      const isDutchAuctionWithDecreasingPrice = info.event_type === 'auction'
        && info.auction_type === 'c'
        && info.price_movement === 'decreases';
      return isSourcingSell || isForwardAuction || isDutchAuctionWithDecreasingPrice;
    })();

    const computeEventSaving = (baseline, bestBid) => (
      isHigherBidBetter ? bestBid - baseline : baseline - bestBid
    );

    const getLineItemEventSavingEntry = (row) => {
      const atLine = buyer_totals?.event_saving_at_line || {};
      const fromAward = award?.line_item_event_saving || {};
      const urid = row?.urid;
      const keyCandidates = [urid, String(urid)];
      if (urid !== undefined && urid !== null && !Number.isNaN(Number(urid))) {
        keyCandidates.push(Number(urid));
      }
      const seen = new Set();
      for (let i = 0; i < keyCandidates.length; i++) {
        const k = keyCandidates[i];
        if (k === undefined || k === null || seen.has(k)) {
          continue;
        }
        seen.add(k);
        const raw = atLine[k]?.event_saving ?? fromAward[k]?.event_saving;
        if (raw !== undefined && raw !== null && raw !== '') {
          return raw;
        }
      }
      return null;
    };

    const isLineItemIncludedInRespondedBaseline = (row) => {
      if (responseNeededForAllLineItems) {
        return true;
      }
      const count = responsesCountByRow?.get?.(row?.urid);
      return !(count === undefined || count === 0);
    };

    /** Sum of line-item baselines for responded rows (Item Sub Total / LTP without lot terms). */
    const getLineItemsBaselineSumNumber = () => {
      if (!Array.isArray(lineItems) || lineItems.length === 0) {
        return NaN;
      }
      let sum = 0;
      let hasAny = false;
      for (let i = 0; i < lineItems.length; i++) {
        const row = lineItems[i];
        if (!isLineItemIncludedInRespondedBaseline(row)) {
          continue;
        }
        const raw = row?.baseline_price;
        if (raw === undefined || raw === null || raw === '') {
          continue;
        }
        const n = Number(String(raw).replace(/,/g, ''));
        if (Number.isNaN(n)) {
          continue;
        }
        sum += n;
        hasAny = true;
      }
      return hasAny ? sum : NaN;
    };

    const hasLotSubTotalFormula = Boolean(
      (lotSubTotal?.actual_formula && String(lotSubTotal.actual_formula).trim())
      || (lotSubTotal?.formula && String(lotSubTotal.formula).trim())
    );

    const evaluateLotFieldFormulaBaseline = (formula, fields) => {
      if (!formula || !String(formula).trim()) {
        return NaN;
      }
      let expr = String(formula);
      for (const field of fields || []) {
        const ucid = field?.ucid || field?.lot_field_id;
        if (!ucid) {
          continue;
        }
        let val = parseNumericCell(field.baseline_price ?? field.baseline_value ?? 0);
        if (Number.isNaN(val)) {
          val = 0;
        }
        if (field.input_field_type === 'Percentage') {
          val /= 100;
        }
        expr = expr.split(`[${ucid}]`).join(String(val));
      }
      try {
        if (!/^[\d\s+\-*/().eE]+$/.test(expr)) {
          return NaN;
        }
        const result = arithmeticParser.evaluate(expr);
        return Number.isFinite(result) ? result : NaN;
      } catch {
        return NaN;
      }
    };

    const getLotSubTotalBaselineNumber = () => {
      if (!hasLotSubTotalFormula) {
        return NaN;
      }
      return evaluateLotFieldFormulaBaseline(
        lotSubTotal?.actual_formula || lotSubTotal?.formula,
        lotLevelFields
      );
    };

    const isBidAction3Lot = bidAction === 'b' || bidAction === '3';

    const getLotTotalPriceBaselineNumber = () => {
      const apiBaseline = parseNumericCell(lotTotalPrice?.baseline_price);
      // Bid action 3: lot total baseline is the line-item sum (totals_row), not lot_total_price.
      // When response is optional, raw lot_total_price baseline includes non-responded rows.
      if (
        !isBidAction3Lot
        && responseNeededForAllLineItems
        && lotTotalPrice?.baseline_price != null
        && lotTotalPrice?.baseline_price !== ''
        && !Number.isNaN(apiBaseline)
      ) {
        return apiBaseline;
      }

      const ltpFormula = lotTotalPrice?.actual_formula || lotTotalPrice?.formula;
      if (ltpFormula && String(ltpFormula).trim()) {
        const itemSubTotal = getLineItemsBaselineSumNumber();
        const lotSubTotalBaseline = getLotSubTotalBaselineNumber();
        const lotSubTotalFromApi = parseNumericCell(lotSubTotal?.baseline_price);
        const lstBaseline = !Number.isNaN(lotSubTotalBaseline)
          ? lotSubTotalBaseline
          : lotSubTotalFromApi;

        const formulaFields = [
          ...(lotLevelFields || []),
          { ucid: 'ist', baseline_price: itemSubTotal, input_field_type: 'Price' },
          { ucid: 'lst', baseline_price: lstBaseline, input_field_type: 'Price' },
        ];
        const evaluated = evaluateLotFieldFormulaBaseline(ltpFormula, formulaFields);
        if (!Number.isNaN(evaluated)) {
          return evaluated;
        }
      }

      const itemSubTotal = getLineItemsBaselineSumNumber();
      const lotSubTotalBaseline = getLotSubTotalBaselineNumber();
      const itemSubTotalNum = Number.isNaN(itemSubTotal) ? 0 : itemSubTotal;
      const lotSubTotalNum = Number.isNaN(lotSubTotalBaseline) ? 0 : lotSubTotalBaseline;
      if (Number.isNaN(itemSubTotal) && Number.isNaN(lotSubTotalBaseline)) {
        return NaN;
      }
      return itemSubTotalNum + lotSubTotalNum;
    };

    const getTransformationPriceBaselineNumber = () => {
      const apiTrpBaseline = parseNumericCell(transformationPrice?.baseline_price);
      if (
        transformationPrice?.baseline_price != null
        && transformationPrice?.baseline_price !== ''
        && !Number.isNaN(apiTrpBaseline)
      ) {
        return apiTrpBaseline;
      }

      const multiplierColumn = (transformedBidColumns || []).find(c => c?.ucid === 'ptf_multiplier') || {};
      const adderColumn = (transformedBidColumns || []).find(c => c?.ucid === 'ptf_adder') || {};
      const multiplier = parseNumericCell(
        multiplierColumn.baseline_price ?? multiplierColumn.baseline_value ?? 1
      );
      const adder = parseNumericCell(
        adderColumn.baseline_price ?? adderColumn.baseline_value ?? 0
      );
      const multiplierNum = Number.isNaN(multiplier) ? 1 : multiplier;
      const adderNum = Number.isNaN(adder) ? 0 : adder;
      const configuredLotTerms = typeof hasLotFields === 'boolean' ? hasLotFields : (lotLevelFields?.length ?? 0) > 0;
      const baseBaseline = configuredLotTerms
        ? getLotTotalPriceBaselineNumber()
        : getLineItemsBaselineSumNumber();
      if (Number.isNaN(baseBaseline)) {
        return NaN;
      }
      return (baseBaseline * multiplierNum) + adderNum;
    };

    const getDerivedLotBaselineDisplay = (rowType) => {
      let baselineNum = NaN;
      if (rowType === 'lot_sub_total') {
        baselineNum = getLotSubTotalBaselineNumber();
      } else if (rowType === 'lot_total_price') {
        baselineNum = getLotTotalPriceBaselineNumber();
      } else if (rowType === 'transformation_price') {
        baselineNum = getTransformationPriceBaselineNumber();
      }
      if (Number.isNaN(baselineNum)) {
        return '';
      }
      return formatPriceValue(baselineNum, 2) || '';
    };
    function renderLotLevelRow(rowIndex, columnIndex, key, style) {
      const lotLevelRowIndex = rowIndex - lineItems.length;
      
      // For lot level rows, we need to get column info based on a valid line item row
      const parentColumn = getColumnInstance(0, columnIndex); // Use first line item for column info
      const columnUcid = parentColumn.safeAccess('ucid');
      const isItemNameColumn = columnUcid === 'item_name';
      const isBidAction3 = bidAction === 'b' || bidAction === '3';
      const isBidAction2 = bidAction === 'c' || bidAction === '2';
      const supplierHasPostAuctionLineItemResponses = (supplierId) => {
        const supplierResponse = data?.[supplierId];
        if (!supplierResponse?.row_data) {
          return false;
        }
        if (Array.isArray(supplierResponse.row_data)) {
          return supplierResponse.row_data.length > 0;
        }
        return Object.keys(supplierResponse.row_data).length > 0;
      };
      const isLotNameColumn = isBidAction3 && columnUcid === 'lot_name';
      const isNameColumn = isItemNameColumn || isLotNameColumn;
      const termName = parentColumn.safeAccess('term_name');
      const lotFieldsCount = lotLevelFields?.length || 0;
      // Prefer API has_lot_fields (matches Lot.has_lot_fields); lot_level_fields array can be non-empty in payloads
      // even when the lot has no configured lot terms — that previously hid Item Sub Total award inputs.
      const hasLotTerms = typeof hasLotFields === 'boolean' ? hasLotFields : lotFieldsCount > 0;
      const shouldHidePostAuctionLineItemBuyerTotals = (
        isBidAction2
        && hasLotTerms
        && !(suppliersOrder || []).some((id) => supplierHasPostAuctionLineItemResponses(id))
      );
      const isLotTransformationEnabled = !!enableLotTransformation;
      const lotSubTotalRowIndex = !isBidAction3 && hasLotTerms ? lotFieldsCount + 3 : -1;
      // No lot-term rows and no TRP: single "lot total" summary is row 0 — put Award % there (legacy UX), not on LTP row.
      // Bid action 3 (lot-level bid) has no lot terms; award % lives on the single summary row.
      const showAwardShareOnItemSubTotalRow = (
        isAwardDraft
        && optimizedAt === 'lot'
        && !isLotTransformationEnabled
        && (isBidAction3 || !hasLotTerms)
      );
      const showAwardShareOnLotTotalPriceRow = (
        isAwardDraft
        && optimizedAt === 'lot'
        && !isLotTransformationEnabled
        && hasLotTerms
      );
      const lotTotalPriceSpacerBeforeRowIndex = isBidAction3 || !hasLotTerms ? -1 : lotFieldsCount + 4;
      const lotTotalPriceRowIndex = isBidAction3 ? 0 : (hasLotTerms ? lotFieldsCount + 5 : -1);
      const isLotSummaryRow = (
        (!isBidAction3 && lotLevelRowIndex === 0)
        || (isBidAction3 && lotLevelRowIndex === lotTotalPriceRowIndex)
      );
      let lotTotalPriceSpacerAfterRowIndex = -1;
      let transformationHeaderRowIndex = -1;
      let multiplierRowIndex = -1;
      let adderRowIndex = -1;
      let transformedPriceRowIndex = -1;
      if (isLotTransformationEnabled) {
        if (isBidAction3) {
          // Bid action 3: no spacer row — LTP + 4 transformation rows fit in lotSummaryAdditionalRows (1 + 4).
          lotTotalPriceSpacerAfterRowIndex = -1;
          transformationHeaderRowIndex = lotTotalPriceRowIndex + 1;
        } else if (hasLotTerms) {
          lotTotalPriceSpacerAfterRowIndex = lotTotalPriceRowIndex + 1;
          transformationHeaderRowIndex = lotTotalPriceRowIndex + 2;
        } else {
          // No lot terms: no gray "spacer" row after Item Sub Total — spacer after LTP sits directly under it.
          lotTotalPriceSpacerAfterRowIndex = 1;
          transformationHeaderRowIndex = 2;
        }
        multiplierRowIndex = transformationHeaderRowIndex + 1;
        adderRowIndex = transformationHeaderRowIndex + 2;
        transformedPriceRowIndex = transformationHeaderRowIndex + 3;
      }
      
      // Item Sub Total / Lot Total Price summary row (bid action 3 uses totals_row, not lot_total_price)
      if (isLotSummaryRow) {
        if (isNameColumn) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={{ textAlign: 'left' }}
              >
                <span className={classNames(tableStyles.cellContent, tableStyles.montserratLabel)} style={{ fontWeight: 'bold' }}>
                  {isBidAction3 || !hasLotTerms ? 'Lot Total Price' : 'Item Sub Total'}
                </span>
              </div>
            </div>
          );
        }
        
        // Handle Item Sub Total for other columns - show all totals from header
        const ucid = parentColumn.ucid;
        const supplierId = parentColumn.safeAccess('supplier_id');
        const isSupplierColumn = supplierId && parentColumn.columns && parentColumn.columns.length > 0;
        
        // Check if this is a buyer column using isBuyerFilled property
        const isBuyerColumn = parentColumn.isBuyerFilled;
        
        if (isBuyerColumn) {
          // For buyer columns, show totals (baseline, event savings, best bid, etc.)
          if (!parentColumn.showTotal) {
            return (
              <div key={key} style={style}>
                <div
                  className={classNames(
                    tableStyles.row,
                    tableStyles.borderRight,
                    tableStyles.supplierValue,
                    tableStyles.padCell
                  )}
                  data-column={termName}
                  data-rowid={rowIndex + 1}
                >
                  <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                  </span>
                </div>
              </div>
            );
          }
          
          let totalValue = '';
          if (ucid === 'baseline_price') {
            const lineBaselineSum = getLineItemsBaselineSumNumber();
            if (!Number.isNaN(lineBaselineSum)) {
              totalValue = formatPriceValue(lineBaselineSum, 2) || '';
            } else if (isBidAction3 && totals_row?.baseline_price) {
              totalValue = formatPriceValue(totals_row.baseline_price, 2) || '';
            } else {
              totalValue = baselineTotal || '';
            }
          } else if (ucid === 'event_saving') {
            if (!shouldHidePostAuctionLineItemBuyerTotals) {
              if (optimizedAt === 'lot' && !hasLotTerms) {
                // With lot TRP, event_saving_at_lot is transformed savings — not LTP.
                // Derive untransformed LTP savings from baseline vs best lot bid.
                if (isLotTransformationEnabled) {
                  const lineBaselineSum = getLineItemsBaselineSumNumber();
                  const baselineNum = !Number.isNaN(lineBaselineSum)
                    ? lineBaselineSum
                    : parseNumericCell(baselineTotal);
                  const bestBidNum = parseNumericCell(bestBidOfLot);
                  if (!Number.isNaN(baselineNum) && !Number.isNaN(bestBidNum)) {
                    totalValue = formatPriceValue(computeEventSaving(baselineNum, bestBidNum), 2) || '';
                  }
                } else {
                  const lotEventSaving = (
                    buyer_totals?.event_saving_at_lot?.event_saving
                    ?? buyer_totals?.event_saving_at_lot_total
                    ?? eventSavingsTotal
                  );
                  const numericLotSaving = parseNumericCell(lotEventSaving);
                  if (!Number.isNaN(numericLotSaving)) {
                    totalValue = formatPriceValue(numericLotSaving, 2) || '';
                  }
                }
              } else if (optimizedAt === 'line') {
                const lineItemEventSavingSummary = (lineItems || []).reduce((acc, row) => {
                  const rowEventSaving = getLineItemEventSavingEntry(row);
                  if (rowEventSaving === undefined || rowEventSaving === null || rowEventSaving === '') {
                    return acc;
                  }
                  const numericSaving = parseFloat(String(rowEventSaving).replace(/,/g, ''));
                  if (Number.isNaN(numericSaving)) {
                    return acc;
                  }
                  return {
                    hasValue: true,
                    value: acc.value + numericSaving
                  };
                }, {
                  hasValue: false,
                  value: 0
                });
                if (lineItemEventSavingSummary.hasValue) {
                  totalValue = formatPriceValue(lineItemEventSavingSummary.value, 2) || '';
                } else {
                  // Lot-level optimization often omits per-line event_saving_at_line; derive from item subtotal baseline vs lot best bid.
                  const lineBaselineSum = getLineItemsBaselineSumNumber();
                  const baselineNum = !Number.isNaN(lineBaselineSum)
                    ? lineBaselineSum
                    : parseNumericCell(baselineTotal);
                  const bestBidNum = parseNumericCell(bestBidOfLot);
                  if (!Number.isNaN(baselineNum) && !Number.isNaN(bestBidNum)) {
                    totalValue = formatPriceValue(computeEventSaving(baselineNum, bestBidNum), 2) || '';
                  } else {
                    totalValue = '';
                  }
                }
              }
            }
          } else if (ucid === 'award_savings') {
            const lineItemAwardSavingSummary = (lineItems || []).reduce((acc, row) => {
              const rowAwardSaving = (
                award?.line_item_award_saving?.[row.urid]
                ?? buyer_totals?.award_saving_at_line?.[row.urid]
              );
              if (rowAwardSaving === undefined || rowAwardSaving === null || rowAwardSaving === '') {
                return acc;
              }
              const numericSaving = parseFloat(String(rowAwardSaving).replace(/,/g, ''));
              if (Number.isNaN(numericSaving)) {
                return acc;
              }
              return {
                hasValue: true,
                value: acc.value + numericSaving
              };
            }, {
              hasValue: false,
              value: 0
            });
            totalValue = lineItemAwardSavingSummary.hasValue
              ? (formatPriceValue(lineItemAwardSavingSummary.value, 2) || '')
              : '';
          } else if (ucid === 'best_bid') {
            if (!shouldHidePostAuctionLineItemBuyerTotals) {
              totalValue = (bestBidOfLot && bestBidOfLot !== '-') ? bestBidOfLot : '';
            }
          } else {
            totalValue = totals_row?.[ucid] || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell,
                  tableStyles.totalCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      parentColumn.isPrice
                      && totalValue !== ''
                      && totalValue !== '-'
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {totalValue && totalValue !== '-' ? totalValue : ''}
                  </span>
                  {
                    parentColumn.isPercentage
                    && totalValue !== ''
                    && totalValue !== '-'
                    && (
                      <span className={tableStyles.suffix}>
                        %
                      </span>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (isSupplierColumn) {
          // For supplier columns, show all totals with ranking
          const subColumns = parentColumn.columns.filter((col) => {
            const colInstance = new Column(col);
            return !hiddenColumnSet.has(colInstance.ucid);
          });
          
          // Add award column if in award draft mode (with input fields for Item Sub Total)
          const subColumnsWithAward = subColumns.slice();
          if (isAwardDraft) {
            subColumnsWithAward.push(
              awardValueColumn({
                awardBy,
                isAwardDraft,
                optimizedAt
              })
            );
          }
          
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  layoutStyles.flex,
                  tableStyles.supplierGroup,
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                {subColumnsWithAward.map((subColumnData, index) => {
                  const column = new Column(subColumnData);
                  const isAwardColumn = column.ucid === 'share';
                  
                  // Match the original header logic for showing totals
                  const supplierTotal = (
                    column.showTotal
                    && !column.isEditable 
                    && getSupplierTotals ? getSupplierTotals(supplierId, subColumnData) : ''
                  );
                  const showValue = !isLotExcluded && (!column.isPTF || (supplierTotal && supplierTotal.length > 0));
                  let hideValue = !showValue;
                  
                  let displayValue = '';
                  let showContent = false;
                  if (column.showTotal && !column.isEditable && !hideValue) {
                    showContent = true;
                    displayValue = supplierTotal || '';
                  }
                  
                  // Get ranking class for lot level totals (exclude PTF columns — no color coding)
                  const rankingClass = (
                    showContent
                    && getRankWiseClassForLot
                    && !column.isPTF
                  )
                    ? getRankWiseClassForLot(supplierId, subColumnData)
                    : null;
                  
                  const isSupplierBlocked = checkIfAwardIsBlocked(participatingSuppliersMap, supplierId);
                  const isDisabled = isLotExcluded || isSupplierBlocked;
                  
                  return (
                    <div
                      key={index}
                      className={classNames(
                        layoutStyles.flex,
                        layoutStyles.flex1,
                        tableStyles.padCell,
                        tableStyles.classicColumn,
                        rankingClass,
                        {
                          [tableStyles.editable]: column.isEditable && column.isLotOptimized && isAwardColumn && showAwardShareOnItemSubTotalRow,
                          [tableStyles.awardShareCellFill]: isAwardColumn && showAwardShareOnItemSubTotalRow
                        }
                      )}
                      data-column={column.termName}
                      data-supplier={parentColumn.safeAccess('supplier_name')}
                      data-rowid={rowIndex + 1}
                    >
                      {showContent ? (
                        <span style={{
                          display: 'flex',
                          justifyContent: column.justifyContent,
                          width: '100%',
                          fontWeight: 'bold'
                        }}>
                          <span>
                            {
                              column.isPrice
                              && displayValue !== ''
                              && currencySymbol
                            }
                          </span>
                          <span className={tableStyles.cellContent}>
                            {displayValue}
                          </span>
                          {
                            column.isPercentage
                            && displayValue !== ''
                            && (
                              <span className={tableStyles.suffix}>
                                %
                              </span>
                            )
                          }
                        </span>
                      ) : isAwardColumn ? (
                        showAwardShareOnItemSubTotalRow ? (
                          <Fragment>
                            <input
                              style={awardColumnStyle}
                              value={getLotShare ? getLotShare(supplierId) : ''}
                              onChange={changeLotShare}
                              data-supplier={supplierId}
                              className={classNames(tableStyles.awardInput, tableStyles.awardInputNoChrome)}
                              disabled={viewAwardScenario || isLotExcluded || checkIfAwardIsBlocked(participatingSuppliersMap, supplierId)}
                            />
                            {
                              !isLotExcluded
                              && column.isLotOptimized
                              && awardBy === '%' ? '%' : null
                            }
                          </Fragment>
                        ) : (
                          <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                          </span>
                        )
                      ) : (
                        <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                {/* Empty cell for item sub total row */}
              </span>
            </div>
          </div>
        );
      }
      
      // Empty row after Item Sub Total — only when lot terms follow (separates item block from Lot Terms header).
      if (!isBidAction3 && lotLevelRowIndex === 1 && hasLotTerms) {
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
              style={{
                backgroundColor: '#fff'
              }}
            >
              {/* Empty row */}
            </div>
          </div>
        );
      }
      
      // Lot Terms header row
      if (!isBidAction3 && hasLotTerms && lotLevelRowIndex === 2) {
        if (isNameColumn) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={{ textAlign: 'left' }}
              >
                <span className={classNames(tableStyles.cellContent, tableStyles.montserratLabel)} style={{ fontWeight: 'bold' }}>
                  Lot Terms
                </span>
              </div>
            </div>
          );
        }
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}>
                {/* Empty cell for lot terms header row */}
              </span>
            </div>
          </div>
        );
      }
      
        // Lot level fields
        if (!isBidAction3 && hasLotTerms && lotLevelFields && lotLevelRowIndex <= lotLevelFields.length + 2) {
          const fieldIndex = lotLevelRowIndex - 3;
          const field = lotLevelFields[fieldIndex];
          const lotFieldIdentifier = `${field?.ucid || field?.lot_field_id || `idx-${fieldIndex}`}`;
          const baselinePayloadId = `lot-field-${lotFieldIdentifier}`;
        
          if (isNameColumn) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={{ textAlign: 'left' }}
              >
                <span className={tableStyles.cellContent}>
                  {field.term_name}
                </span>
              </div>
            </div>
          );
          }
        
          // For other columns, show the field value if applicable
          const ucid = parentColumn.safeAccess('ucid');
          const supplierId = parentColumn.safeAccess('supplier_id');
        
          // Check if this is a supplier column (has sub-columns)
          const isSupplierColumn = supplierId && parentColumn.columns && parentColumn.columns.length > 0;
        
          if (isSupplierColumn) {
          // For supplier columns, span across all sub-columns like regular line items
          const subColumns = parentColumn.columns.filter(({ ucid }) => !hiddenColumnSet.has(ucid));
          
          // Add award column if in award draft mode (empty/blank for lot level fields)
          const subColumnsWithAward = subColumns.slice();
          if (isAwardDraft) {
            subColumnsWithAward.push(
              awardValueColumn({
                awardBy,
                isAwardDraft,
                optimizedAt
              })
            );
          }
          
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  layoutStyles.flex,
                  tableStyles.supplierGroup,
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                {subColumnsWithAward.map((subColumn, index) => {
                  const column = new Column(subColumn);
                  const isAwardColumn = column.ucid === 'share';
                  let displayValue = '';
                  let showContent = false;
                  
                  // Award column should be empty/blank for lot level fields
                  if (isAwardColumn) {
                    showContent = false;
                  } else if (subColumn.ucid === bestBidColumnId) {
                    showContent = true;
                    // Check for supplier response for this lot field
                    if (data && data[supplierId] && data[supplierId].lot_field_responses) {
                      const lotFieldResponses = data[supplierId].lot_field_responses;
                      if (lotFieldResponses && lotFieldResponses[field.ucid] !== undefined && lotFieldResponses[field.ucid] !== '') {
                        displayValue = lotFieldResponses[field.ucid];
                      }
                    }
                  } else if (subColumn.ucid === 'baseline_price') {
                    showContent = true;
                    // Text lot terms have no baseline
                    if (isLotFieldTextType(field)) {
                      displayValue = '';
                    } else {
                      displayValue = field.baseline_value || field.baseline_price || field.value || '';
                      // Ensure proper decimal formatting for baseline values
                      if (displayValue && typeof displayValue === 'number') {
                        displayValue = displayValue.toFixed(2);
                      } else if (displayValue && typeof displayValue === 'string' && displayValue.trim() !== '') {
                        const numValue = parseNumericCell(displayValue);
                        if (!Number.isNaN(numValue)) {
                          displayValue = numValue.toFixed(2);
                        }
                      }
                    }
                  } else if (subColumn.ucid === 'percentage_away_from_best_bid') {
                    // % above/below best bid only for price lot terms
                    if (isLotFieldPriceType(field)) {
                      showContent = true;
                      if (data && data[supplierId] && data[supplierId].lot_field_percentage_differences) {
                        displayValue = data[supplierId].lot_field_percentage_differences[field.ucid] || '';
                      }
                    }
                  }
                  
                  const isLotFieldPtf = field?.price_transformation_factor === 'yes';
                  const isLotFieldPrice = isLotFieldPriceType(field);
                  // Color coding only for price lot terms (skip PTF and non-price types)
                  const rankingClass = (
                    showContent
                    && checkLotFieldRanking
                    && isLotFieldPrice
                    && !isLotFieldPtf
                  )
                    ? checkLotFieldRanking(field.ucid, supplierId, subColumn.ucid)
                    : null;
                  
                  return (
                    <div
                      key={index}
                      className={classNames(
                        layoutStyles.flex,
                        layoutStyles.flex1,
                        tableStyles.padCell,
                        tableStyles.classicColumn,
                        rankingClass
                      )}
                      data-column={column.termName}
                      data-supplier={parentColumn.safeAccess('supplier_name')}
                      data-rowid={rowIndex + 1}
                    >
                      {showContent ? (
                        <span style={{
                          display: 'flex',
                          justifyContent: getPercentBaselineExtendedJustify(
                            field,
                            subColumn.ucid,
                            column.justifyContent,
                            bestBidColumnId
                          ),
                          width: '100%'
                        }}>
                          {
                            isPercentInBaselineOrExtended(field, subColumn.ucid, bestBidColumnId) ? (
                              <PercentValueWithSuffix value={displayValue} />
                            ) : (
                              <Fragment>
                                <span>
                                  {
                                    field.input_field_type === 'Price'
                                    && subColumn.ucid !== 'percentage_away_from_best_bid'
                                    && displayValue !== ''
                                    && currencySymbol
                                  }
                                </span>
                                <span className={tableStyles.cellContent}>
                                  {displayValue}
                                </span>
                                {
                                  subColumn.ucid === 'percentage_away_from_best_bid'
                                  && displayValue !== ''
                                  && (
                                    <span className={tableStyles.suffix}>
                                      %
                                    </span>
                                  )
                                }
                              </Fragment>
                            )
                          }
                        </span>
                      ) : isAwardColumn ? (
                        // Empty award column for lot level fields
                        <span className={tableStyles.cellContent}>
                        </span>
                      ) : (
                        <span className={tableStyles.cellContent}>
                          {/* Empty cell for non-matching columns */}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        } else if (ucid === bestBidColumnId) {
          // Show lot field values only in best bid column
          let displayValue = field.baseline_value || field.value || '';
          
          // Check if there's a supplier response for this field
          if (supplierId && data && data[supplierId] && data[supplierId].lot_field_responses) {
            // For lot level fields, they are stored in lot_field_responses
            const lotFieldResponses = data[supplierId].lot_field_responses;
            if (lotFieldResponses && lotFieldResponses[field.ucid] !== undefined && lotFieldResponses[field.ucid] !== '') {
              displayValue = lotFieldResponses[field.ucid];
            }
          }
          
          const isLotFieldPtfBuyer = field?.price_transformation_factor === 'yes';
          const isLotFieldPrice = field?.input_field_type === 'Price';
          const rankingClass = (
            checkLotFieldRanking
            && isLotFieldPrice
            && !isLotFieldPtfBuyer
          )
            ? checkLotFieldRanking(field.ucid, supplierId, ucid)
            : null;
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell,
                  rankingClass
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: getPercentBaselineExtendedJustify(
                    field,
                    ucid,
                    parentColumn.justifyContent,
                    bestBidColumnId
                  ),
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  {
                    isPercentInBaselineOrExtended(field, ucid, bestBidColumnId) ? (
                      <PercentValueWithSuffix value={displayValue} />
                    ) : (
                      <Fragment>
                        <span>
                          {
                            field.input_field_type === 'Price'
                            && displayValue !== ''
                            && currencySymbol
                          }
                        </span>
                        <span className={tableStyles.cellContent}>
                          {displayValue}
                        </span>
                        {
                          field.input_field_type === 'Percentage'
                          && displayValue !== ''
                          && (
                            <span className={tableStyles.suffix}>
                              %
                            </span>
                          )
                        }
                      </Fragment>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'best_bid') {
          // Best bid applies only to price lot terms
          let displayValue = '';
          if (
            isLotFieldPriceType(field)
            && bestBids
            && bestBids.get('lotLevelFieldsBestBids')
          ) {
            const lotLevelFieldsBestBids = bestBids.get('lotLevelFieldsBestBids');
            displayValue = lotLevelFieldsBestBids.get(field.ucid) || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: getPercentBaselineExtendedJustify(
                    field,
                    ucid,
                    parentColumn.justifyContent,
                    bestBidColumnId
                  ),
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  {
                    isPercentInBaselineOrExtended(field, ucid, bestBidColumnId) ? (
                      <PercentValueWithSuffix value={displayValue} />
                    ) : (
                      <Fragment>
                        <span>
                          {
                            field.input_field_type === 'Price'
                            && displayValue !== ''
                            && currencySymbol
                          }
                        </span>
                        <span className={tableStyles.cellContent}>
                          {displayValue}
                        </span>
                        {
                          field.input_field_type === 'Percentage'
                          && displayValue !== ''
                          && (
                            <span className={tableStyles.suffix}>
                              %
                            </span>
                          )
                        }
                      </Fragment>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'baseline_price') {
          // Text lot terms have no baseline (buyer- or supplier-filled)
          const isTextLotField = isLotFieldTextType(field);
          let displayValue = '';
          if (!isTextLotField) {
            displayValue = field.baseline_value || field.baseline_price || field.value || '';
            if (displayValue && typeof displayValue === 'number') {
              displayValue = displayValue.toFixed(2);
            } else if (displayValue && typeof displayValue === 'string' && displayValue.trim() !== '') {
              const numValue = parseNumericCell(displayValue);
              if (!Number.isNaN(numValue)) {
                displayValue = numValue.toFixed(2);
              }
            }
          }
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell,
                  {
                    [tableStyles.editable]: isColumnEditable && !isTextLotField
                  }
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: getPercentBaselineExtendedJustify(
                    field,
                    ucid,
                    parentColumn.justifyContent,
                    bestBidColumnId
                  ),
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  {
                    field.input_field_type === 'Percentage' ? (
                      isColumnEditable ? (
                        <span className={tableStyles.percentValueGroup}>
                          <input
                            style={baselineColumnStyle}
                            className={tableStyles.awardInput}
                            type="text"
                            id={`baseline-${baselinePayloadId}`}
                            defaultValue={displayValue}
                            onChange={(e) => handleBaselinePayload && handleBaselinePayload(e, baselinePayloadId, rowIndex, ucid)}
                            onBlur={(e) => handleFormatBaseline && handleFormatBaseline(e, rowIndex, columnIndex, baselinePayloadId, ucid)}
                            onPaste={(e) => handleBaselinePaste && handleBaselinePaste(e, rowIndex, columnIndex, ucid)}
                          />
                          {displayValue !== '' && <span className={tableStyles.suffixTight}>%</span>}
                        </span>
                      ) : (
                        <PercentValueWithSuffix value={displayValue} />
                      )
                    ) : (
                      <Fragment>
                        <span>
                          {
                            field.input_field_type === 'Price'
                            && displayValue !== ''
                            && currencySymbol
                          }
                        </span>
                        {
                          isColumnEditable ? (
                            <input
                              style={baselineColumnStyle}
                              className={tableStyles.awardInput}
                              type="text"
                              id={`baseline-${baselinePayloadId}`}
                              defaultValue={displayValue}
                              onChange={(e) => handleBaselinePayload && handleBaselinePayload(e, baselinePayloadId, rowIndex, ucid)}
                              onBlur={(e) => handleFormatBaseline && handleFormatBaseline(e, rowIndex, columnIndex, baselinePayloadId, ucid)}
                              onPaste={(e) => handleBaselinePaste && handleBaselinePaste(e, rowIndex, columnIndex, ucid)}
                            />
                          ) : (
                            <span className={tableStyles.cellContent}>
                              {displayValue}
                            </span>
                          )
                        }
                      </Fragment>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'percentage_away_from_best_bid') {
          // % above/below best bid only for price lot terms
          let displayValue = '';
          if (
            isLotFieldPriceType(field)
            && supplierId
            && data
            && data[supplierId]
            && data[supplierId].lot_field_percentage_differences
          ) {
            displayValue = data[supplierId].lot_field_percentage_differences[field.ucid] || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  <span className={tableStyles.cellContent}>
                    {displayValue}
                  </span>
                  {
                    displayValue !== ''
                    && (
                      <span className={tableStyles.suffix}>
                        %
                      </span>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'event_saving' && parentColumn.isBuyerFilled) {
          // Event savings applies only to price lot terms
          let displayValue = '';
          if (isLotFieldPriceType(field)) {
            const baselineParsed = parseNumericCell(field.baseline_value || field.baseline_price || field.value || 0);
            const baselineValue = Number.isNaN(baselineParsed) ? 0 : baselineParsed;
            let bestBidValue = 0;

            if (bestBids && bestBids.get('lotLevelFieldsBestBids')) {
              const lotLevelFieldsBestBids = bestBids.get('lotLevelFieldsBestBids');
              const bestBidStr = lotLevelFieldsBestBids.get(field.ucid) || '';
              bestBidValue = parseFloat(bestBidStr.toString().replace(/,/g, '')) || 0;
            }

            if (baselineValue && bestBidValue) {
              const eventSaving = computeEventSaving(baselineValue, bestBidValue);
              if (eventSaving !== 0) {
                displayValue = eventSaving.toFixed(2);
                displayValue = parseFloat(displayValue).toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
            }
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && field.input_field_type === 'Price'
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || (isLotFieldPriceType(field) ? '-' : '')}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'award_savings' && parentColumn.isBuyerFilled) {
          const awardSavingRaw = (
            award?.line_item_award_saving?.[field.ucid]
            ?? award?.line_item_event_saving?.[field.ucid]?.event_saving
          );
          const displayValue = (
            awardSavingRaw === undefined || awardSavingRaw === null || awardSavingRaw === ''
              ? ''
              : formatPriceValue(awardSavingRaw, 2)
          );
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && field.input_field_type === 'Price'
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        } else {
          // For other columns (not baseline_price, not best_bid_column, not percentage), show empty for lot fields
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span className={tableStyles.cellContent}>
                  {/* Empty for lot fields in non-extended-price columns */}
                </span>
              </div>
            </div>
          );
          }
        
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span className={tableStyles.cellContent}>
                  {/* Empty cell for lot level fields */}
                </span>
              </div>
            </div>
          );
      }
      
        // Lot Sub Total row
        if (!isBidAction3 && lotLevelRowIndex === lotSubTotalRowIndex) {
        if (isNameColumn) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={{ textAlign: 'left' }}
              >
                <span className={classNames(tableStyles.cellContent, tableStyles.montserratLabel)} style={{ fontWeight: 'bold' }}>
                  Lot Sub Total
                  <Formula formula={lotSubTotal?.formula} />
                </span>
              </div>
            </div>
          );
        }

        if (!hasLotSubTotalFormula) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span className={tableStyles.cellContent}>
                  {/* Empty cell for lot sub total row when formula is not specified */}
                </span>
              </div>
            </div>
          );
        }

        // Check if this is a supplier column for Lot Sub Total
        const ucid = parentColumn.safeAccess('ucid');
        const supplierId = parentColumn.safeAccess('supplier_id');
        const isSupplierColumn = supplierId && parentColumn.columns && parentColumn.columns.length > 0;
        
        if (isSupplierColumn) {
          // For supplier columns, span across all sub-columns
          const subColumns = parentColumn.columns.filter(({ ucid }) => !hiddenColumnSet.has(ucid));
          
          // Add award column if in award draft mode (empty/blank for Lot Sub Total)
          const subColumnsWithAward = subColumns.slice();
          if (isAwardDraft) {
            subColumnsWithAward.push(
              awardValueColumn({
                awardBy,
                isAwardDraft,
                optimizedAt
              })
            );
          }
          
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  layoutStyles.flex,
                  tableStyles.supplierGroup,
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                {subColumnsWithAward.map((subColumn, index) => {
                  const column = new Column(subColumn);
                  const isAwardColumn = column.ucid === 'share';
                  let displayValue = '';
                  let showContent = false;
                  
                  // Award column should be empty/blank for Lot Sub Total
                  if (isAwardColumn) {
                    showContent = false;
                  } else if (subColumn.ucid === 'extended_price') {
                    showContent = true;
                    // BA2 with lot terms: lot sub total blank until post-auction line items submitted
                    if (
                      !(isBidAction2 && hasLotTerms && !supplierHasPostAuctionLineItemResponses(supplierId))
                      && data
                      && data[supplierId]
                      && data[supplierId].lot_sub_total !== undefined
                    ) {
                      const rawValue = data[supplierId].lot_sub_total;
                      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                        displayValue = formatPriceValue(rawValue, 2);
                      }
                    }
                  } else if (subColumn.ucid === 'percentage_away_from_best_bid') {
                    showContent = true;
                    // Show percentage away from best bid for LST
                    if (
                      !(isBidAction2 && hasLotTerms && !supplierHasPostAuctionLineItemResponses(supplierId))
                      && data
                      && data[supplierId]
                      && data[supplierId].lst_ltp_percentage_differences
                    ) {
                      displayValue = data[supplierId].lst_ltp_percentage_differences['lot_sub_total'] || '';
                    }
                  }
                  
                  // Get ranking class for LST when optimized at line level
                  const lstRankingClass = (
                    showContent
                    && displayValue !== ''
                    && checkLSTRanking
                  )
                    ? checkLSTRanking(supplierId, subColumn.ucid)
                    : null;
                  
                  return (
                    <div
                      key={index}
                      className={classNames(
                        layoutStyles.flex,
                        layoutStyles.flex1,
                        tableStyles.padCell,
                        tableStyles.classicColumn,
                        lstRankingClass
                      )}
                      data-column={column.termName}
                      data-supplier={parentColumn.safeAccess('supplier_name')}
                      data-rowid={rowIndex + 1}
                    >
                      {showContent ? (
                        <span style={{
                          display: 'flex',
                          justifyContent: column.justifyContent,
                          width: '100%',
                          fontWeight: 'bold'
                        }}>
                          <span>
                            {
                              column.isPrice
                              && subColumn.ucid !== 'percentage_away_from_best_bid'
                              && displayValue !== ''
                              && currencySymbol
                            }
                          </span>
                          <span className={tableStyles.cellContent}>
                            {displayValue}
                          </span>
                          {
                            subColumn.ucid === 'percentage_away_from_best_bid'
                            && displayValue !== ''
                            && (
                              <span className={tableStyles.suffix}>
                                %
                              </span>
                            )
                          }
                        </span>
                      ) : isAwardColumn ? (
                        // Empty award column for Lot Sub Total
                        <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                        </span>
                      ) : (
                        <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                          {/* Empty cell for non-extended-price columns */}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        } else if (ucid === 'best_bid') {
          // For best bid column in LST row, show the best LST bid
          let displayValue = '';
          if (bestBids && bestBids.get('lstLtpBestBids')) {
            const lstLtpBestBids = bestBids.get('lstLtpBestBids');
            displayValue = lstLtpBestBids.get('lot_sub_total') || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'percentage_away_from_best_bid') {
          // Show percentage away from best bid for LST
          let displayValue = '';
          if (supplierId && data && data[supplierId] && data[supplierId].lst_ltp_percentage_differences) {
            displayValue = data[supplierId].lst_ltp_percentage_differences['lot_sub_total'] || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span className={tableStyles.cellContent}>
                    {displayValue}
                  </span>
                  {
                    displayValue !== ''
                    && (
                      <span className={tableStyles.suffix}>
                        %
                      </span>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'extended_price' || ucid === 'baseline_price') {
          let totalValue = ucid === 'baseline_price'
            ? (getDerivedLotBaselineDisplay('lot_sub_total') || formatPriceValue(lotSubTotal?.baseline_price, 2))
            : lotSubTotal?.value;
          if (ucid !== 'baseline_price') {
            totalValue = formatPriceValue(totalValue, 2);
          }
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      totalValue !== '-'
                      && totalValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {totalValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'event_saving' && parentColumn.isBuyerFilled) {
          // Show event savings for Lot Sub Total
          // Event Savings = baseline_price - best_bid (for lot_sub_total)
          let displayValue = '';
          const derivedLotSubTotalBaseline = getLotSubTotalBaselineNumber();
          const baselineParsed = !Number.isNaN(derivedLotSubTotalBaseline)
            ? derivedLotSubTotalBaseline
            : parseNumericCell(lotSubTotal?.baseline_price || 0);
          const baselineValue = Number.isNaN(baselineParsed) ? 0 : baselineParsed;
          let bestBidValue = 0;
          
          if (bestBids && bestBids.get('lstLtpBestBids')) {
            const lstLtpBestBids = bestBids.get('lstLtpBestBids');
            const bestBidStr = lstLtpBestBids.get('lot_sub_total') || '';
            bestBidValue = parseFloat(bestBidStr.toString().replace(/,/g, '')) || 0;
          }
          
          if (baselineValue && bestBidValue) {
            const eventSaving = computeEventSaving(baselineValue, bestBidValue);
            if (eventSaving !== 0) {
              displayValue = eventSaving.toFixed(2);
              displayValue = parseFloat(displayValue).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'award_savings' && parentColumn.isBuyerFilled) {
          const awardSavingRaw = (
            award?.award_saving_at_lot_sub_total?.award_saving
            ?? award?.line_item_award_saving?.lot_sub_total
          );
          const displayValue = (
            awardSavingRaw === undefined || awardSavingRaw === null || awardSavingRaw === ''
              ? ''
              : formatPriceValue(awardSavingRaw, 2)
          );
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        }
        
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}>
                {/* Empty cell for lot sub total row */}
              </span>
            </div>
          </div>
        );
      }
      
        // Empty row before Lot Total Price
        if (!isBidAction3 && lotLevelRowIndex === lotTotalPriceSpacerBeforeRowIndex) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span className={tableStyles.cellContent}>
                  {/* Spacer row before lot total price */}
                </span>
              </div>
            </div>
          );
        }

        // Lot Total Price row (skipped for bid action 3 — handled in isLotSummaryRow above)
        if (lotLevelRowIndex === lotTotalPriceRowIndex && !isBidAction3) {
        if (isNameColumn) {
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={{ textAlign: 'left' }}
              >
                <span className={classNames(tableStyles.cellContent, tableStyles.montserratLabel)} style={{ fontWeight: 'bold' }}>
                  Lot Total Price
                  {!isBidAction3 && (
                    <Formula formula={lotTotalPrice?.formula} />
                  )}
                </span>
              </div>
            </div>
          );
        }
        
        // Check if this is a supplier column for Lot Total Price
        const ucid = parentColumn.safeAccess('ucid');
        const supplierId = parentColumn.safeAccess('supplier_id');
        const isSupplierColumn = supplierId && parentColumn.columns && parentColumn.columns.length > 0;
        
        if (isSupplierColumn) {
          // For supplier columns, span across all sub-columns
          const subColumns = parentColumn.columns.filter(({ ucid }) => !hiddenColumnSet.has(ucid));
          
          // Add award column if in award draft mode (with input fields for Lot Total Price)
          const subColumnsWithAward = subColumns.slice();
          if (isAwardDraft) {
            subColumnsWithAward.push(
              awardValueColumn({
                awardBy,
                isAwardDraft,
                optimizedAt
              })
            );
          }
          
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  layoutStyles.flex,
                  tableStyles.supplierGroup,
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                {subColumnsWithAward.map((subColumn, index) => {
                  const column = new Column(subColumn);
                  const isAwardColumn = column.ucid === 'share';
                  let displayValue = '';
                  let showContent = false;
                  
                  // Show lot total price only for extended_price column in supplier responses
                  if (subColumn.ucid === 'extended_price') {
                    showContent = true;
                    // Check if there's a supplier response for lot total price
                    if (data && data[supplierId] && data[supplierId].lot_total_price !== undefined) {
                      const rawValue = data[supplierId].lot_total_price;
                      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                        displayValue = formatPriceValue(rawValue, 2);
                      }
                    }
                  } else if (subColumn.ucid === 'percentage_away_from_best_bid') {
                    showContent = true;
                    // Show percentage away from best bid for LTP
                    if (data && data[supplierId] && data[supplierId].lst_ltp_percentage_differences) {
                      displayValue = data[supplierId].lst_ltp_percentage_differences['lot_total_price'] || '';
                    }
                  }
                  
                  // Get ranking class for LTP when optimized at lot level
                  const ltpRankingClass = showContent && checkLTPRanking ? 
                    checkLTPRanking(supplierId, subColumn.ucid) : null;
                  
                  return (
                    <div
                      key={index}
                      className={classNames(
                        layoutStyles.flex,
                        layoutStyles.flex1,
                        tableStyles.padCell,
                        tableStyles.classicColumn,
                        ltpRankingClass,
                        {
                          [tableStyles.editable]: isAwardColumn && showAwardShareOnLotTotalPriceRow && column.isEditable && column.isLotOptimized,
                          [tableStyles.awardShareCellFill]: isAwardColumn && showAwardShareOnLotTotalPriceRow
                        }
                      )}
                      data-column={column.termName}
                      data-supplier={parentColumn.safeAccess('supplier_name')}
                      data-rowid={rowIndex + 1}
                    >
                      {showContent ? (
                        <span style={{
                          display: 'flex',
                          justifyContent: column.justifyContent,
                          width: '100%',
                          fontWeight: 'bold'
                        }}>
                          <span>
                            {
                              column.isPrice
                              && subColumn.ucid !== 'percentage_away_from_best_bid'
                              && displayValue !== ''
                              && currencySymbol
                            }
                          </span>
                          <span className={tableStyles.cellContent}>
                            {displayValue}
                          </span>
                          {
                            subColumn.ucid === 'percentage_away_from_best_bid'
                            && displayValue !== ''
                            && (
                              <span className={tableStyles.suffix}>
                                %
                              </span>
                            )
                          }
                        </span>
                      ) : isAwardColumn ? (
                        showAwardShareOnLotTotalPriceRow ? (
                          // Award column with input fields for Lot Total Price
                          <Fragment>
                            <input
                              style={awardColumnStyle}
                              value={getLotShare ? getLotShare(supplierId) : ''}
                              onChange={changeLotShare}
                              data-supplier={supplierId}
                              className={classNames(tableStyles.awardInput, tableStyles.awardInputNoChrome)}
                              disabled={viewAwardScenario || isLotExcluded || checkIfAwardIsBlocked(participatingSuppliersMap, supplierId)}
                            />
                            {
                              !isLotExcluded
                              && column.isLotOptimized
                              && awardBy === '%' ? '%' : null
                            }
                          </Fragment>
                        ) : (
                          <span className={tableStyles.cellContent}></span>
                        )
                      ) : (
                        <span className={tableStyles.cellContent} style={{ fontWeight: 'bold' }}>
                          {/* Empty cell for non-extended-price columns */}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        } else if (ucid === 'best_bid') {
          // For best bid column in LTP row, show the best LTP bid
          let displayValue = '';
          if (bestBids && bestBids.get('lstLtpBestBids')) {
            const lstLtpBestBids = bestBids.get('lstLtpBestBids');
            displayValue = lstLtpBestBids.get('lot_total_price') || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'percentage_away_from_best_bid') {
          // Show percentage away from best bid for LTP
          let displayValue = '';
          if (supplierId && data && data[supplierId] && data[supplierId].lst_ltp_percentage_differences) {
            displayValue = data[supplierId].lst_ltp_percentage_differences['lot_total_price'] || '';
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span className={tableStyles.cellContent}>
                    {displayValue}
                  </span>
                  {
                    displayValue !== ''
                    && (
                      <span className={tableStyles.suffix}>
                        %
                      </span>
                    )
                  }
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'extended_price' || ucid === 'baseline_price') {
          let totalValue = ucid === 'baseline_price'
            ? getDerivedLotBaselineDisplay('lot_total_price')
            : lotTotalPrice?.value;
          if (ucid !== 'baseline_price') {
            totalValue = formatPriceValue(totalValue, 2);
          }
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      totalValue !== '-'
                      && totalValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {totalValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'event_saving' && parentColumn.isBuyerFilled) {
          // Show event savings for Lot Total Price
          // Event Savings = baseline_price - best_bid (for lot_total_price)
          let displayValue = '';
          const baselineParsed = getLotTotalPriceBaselineNumber();
          const baselineValue = Number.isNaN(baselineParsed) ? 0 : baselineParsed;
          let bestBidValue = 0;
          
          if (bestBids && bestBids.get('lstLtpBestBids')) {
            const lstLtpBestBids = bestBids.get('lstLtpBestBids');
            const bestBidStr = lstLtpBestBids.get('lot_total_price') || '';
            bestBidValue = parseFloat(bestBidStr.toString().replace(/,/g, '')) || 0;
          }
          
          if (baselineValue && bestBidValue) {
            const eventSaving = computeEventSaving(baselineValue, bestBidValue);
            if (eventSaving !== 0) {
              displayValue = eventSaving.toFixed(2);
              displayValue = parseFloat(displayValue).toLocaleString('en-IN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            }
          }
          
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        } else if (ucid === 'award_savings' && parentColumn.isBuyerFilled) {
          const awardSavingRaw = (
            award?.award_saving_at_lot_total_price?.award_saving
            ?? award?.lot_award_saving
          );
          const displayValue = (
            awardSavingRaw === undefined || awardSavingRaw === null || awardSavingRaw === ''
              ? ''
              : formatPriceValue(awardSavingRaw, 2)
          );
          return (
            <div
              key={key}
              style={style}
            >
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}>
                  <span>
                    {
                      displayValue !== ''
                      && currencySymbol
                    }
                  </span>
                  <span className={tableStyles.cellContent}>
                    {displayValue || '-'}
                  </span>
                </span>
              </div>
            </div>
          );
        }
        
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}>
                {/* Empty cell for lot total price row */}
              </span>
            </div>
          </div>
        );
      }

      // Empty row after Lot Total Price (only when transformation is enabled)
      if (isLotTransformationEnabled && lotLevelRowIndex === lotTotalPriceSpacerAfterRowIndex) {
        return (
          <div
            key={key}
            style={style}
          >
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}>
                {/* Spacer row after lot total price */}
              </span>
            </div>
          </div>
        );
      }

      // Transformation rows (header + multiplier + adder + transformed price)
      if (
        isLotTransformationEnabled
        && lotLevelRowIndex >= transformationHeaderRowIndex
        && lotLevelRowIndex <= transformedPriceRowIndex
      ) {
        const ucid = parentColumn.safeAccess('ucid');
        const supplierId = parentColumn.safeAccess('supplier_id');
        const isSupplierColumn = supplierId && parentColumn.columns && parentColumn.columns.length > 0;
        const isHeaderRow = lotLevelRowIndex === transformationHeaderRowIndex;
        const isMultiplierRow = lotLevelRowIndex === multiplierRowIndex;
        const isAdderRow = lotLevelRowIndex === adderRowIndex;
        const isTrpRow = lotLevelRowIndex === transformedPriceRowIndex;
        const multiplierColumn = (transformedBidColumns || []).find(c => c?.ucid === 'ptf_multiplier') || {};
        const adderColumn = (transformedBidColumns || []).find(c => c?.ucid === 'ptf_adder') || {};
        const isSupplierBidPriceSubColumn = (subColumnUcid) => (
          subColumnUcid === 'extended_price'
          || subColumnUcid === 'tranformed_price'
          || subColumnUcid === 'total_cost'
          || subColumnUcid === bestBidColumnId
        );
        const getSupplierTrpValue = (supplierResponse) => {
          if (!supplierResponse) {
            return '';
          }
          const raw = (
            supplierResponse.transformation_price
            ?? supplierResponse.totals_row?.transformation_price
            ?? supplierResponse.totals_row?.tranformed_price
          );
          return raw !== undefined && raw !== null && raw !== '' ? raw : '';
        };
        const getTransformationBaselineValue = (columnData, fallbackValue) => {
          const value = [
            columnData?.baseline_value,
            columnData?.baseline_price,
            columnData?.value,
            columnData?.default_value,
          ].find(item => item !== undefined && item !== null && item !== '');
          return value !== undefined ? value : fallbackValue;
        };

        if (isNameColumn) {
          let label = '';
          if (isHeaderRow) label = 'Price Transformation Factors';
          if (isMultiplierRow) label = multiplierColumn.term_name || 'Multiplier';
          if (isAdderRow) label = adderColumn.term_name || 'Adder';
          if (isTrpRow) label = (transformationPrice?.term_name || 'Transformed Price');
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
                style={(isHeaderRow || isMultiplierRow || isAdderRow || isTrpRow) ? { textAlign: 'left' } : undefined}
              >
                <span
                  className={classNames(tableStyles.cellContent, {
                    [tableStyles.montserratLabel]: isHeaderRow || isTrpRow
                  })}
                  style={{ fontWeight: isHeaderRow || isTrpRow ? 'bold' : undefined }}
                >
                  {label}
                  {isTrpRow && <Formula formula={transformationPrice?.formula || 'TRP = LTP x Multiplier + Adder'} />}
                </span>
              </div>
            </div>
          );
        }

        if (isSupplierColumn) {
          const supplierKey = supplierId.toString().split('_')[0];
          const subColumns = parentColumn.columns.filter(({ ucid }) => !hiddenColumnSet.has(ucid));
          const subColumnsWithAward = subColumns.slice();
          if (isAwardDraft) {
            subColumnsWithAward.push(
              awardValueColumn({
                awardBy,
                isAwardDraft,
                optimizedAt
              })
            );
          }
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  layoutStyles.flex,
                  tableStyles.supplierGroup,
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                {subColumnsWithAward.map((subColumn, index) => {
                  const column = new Column(subColumn);
                  const isAwardColumn = column.ucid === 'share';
                  let value = '';
                  if (!isHeaderRow && !isAwardColumn && isSupplierBidPriceSubColumn(subColumn.ucid)) {
                    if (isMultiplierRow) value = (transformationCostsData?.[supplierKey]?.ptf_multiplier || '');
                    if (isAdderRow) value = (transformationCostsData?.[supplierKey]?.ptf_adder || '');
                    if (isTrpRow) {
                      value = getSupplierTrpValue(data?.[supplierId]);
                      value = formatPriceValue(value, 2);
                    }
                  } else if (!isHeaderRow && !isAwardColumn && isTrpRow && subColumn.ucid === 'percentage_away_from_best_bid') {
                    value = (data?.[supplierId]?.lst_ltp_percentage_differences?.transformation_price) || '';
                  }
                  const trpRankingClass = (
                    isTrpRow
                    && checkTRPRanking
                    && isSupplierBidPriceSubColumn(subColumn.ucid)
                    && value
                  )
                    ? checkTRPRanking(supplierId, subColumn.ucid)
                    : null;
                  const isPtfMultiplierOrAdderRow = isMultiplierRow || isAdderRow;
                  return (
                    <div
                      key={index}
                      className={classNames(
                        layoutStyles.flex,
                        layoutStyles.flex1,
                        tableStyles.padCell,
                        tableStyles.classicColumn,
                        trpRankingClass,
                        {
                          [tableStyles.awardShareCellFill]: isTrpRow && isAwardColumn
                        }
                      )}
                      data-column={column.termName}
                      data-supplier={parentColumn.safeAccess('supplier_name')}
                      data-rowid={rowIndex + 1}
                    >
                      {
                        isAwardColumn ? (
                          isTrpRow ? (
                            <Fragment>
                              <input
                                style={awardColumnStyle}
                                value={getLotShare ? getLotShare(supplierId) : ''}
                                onChange={changeLotShare}
                                data-supplier={supplierId}
                                className={classNames(tableStyles.awardInput, tableStyles.awardInputNoChrome)}
                                disabled={viewAwardScenario || isLotExcluded || checkIfAwardIsBlocked(participatingSuppliersMap, supplierId)}
                              />
                              {
                                !isLotExcluded
                                && column.isLotOptimized
                                && awardBy === '%' ? '%' : null
                              }
                            </Fragment>
                          ) : (
                            <span className={tableStyles.cellContent}></span>
                          )
                        ) : (
                          <span style={{
                            display: 'flex',
                            justifyContent: isPtfMultiplierOrAdderRow ? 'flex-end' : column.justifyContent,
                            width: '100%',
                            fontWeight: isTrpRow ? 'bold' : undefined
                          }}>
                            <span>{isTrpRow && column.isPrice && value ? currencySymbol : ''}</span>
                            <span className={tableStyles.cellContent}>{value}</span>
                            {
                              isTrpRow
                              && subColumn.ucid === 'percentage_away_from_best_bid'
                              && value !== ''
                              ? <span className={tableStyles.suffix}>%</span>
                              : null
                            }
                          </span>
                        )
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        if ((isMultiplierRow || isAdderRow) && ucid === 'baseline_price') {
          const value = isMultiplierRow
            ? getTransformationBaselineValue(multiplierColumn, '1.000')
            : getTransformationBaselineValue(adderColumn, '0.00');
          const baselinePayloadId = isMultiplierRow ? 'ptf_multiplier' : 'ptf_adder';
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell,
                  {
                    [tableStyles.editable]: isColumnEditable
                  }
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  {
                    isColumnEditable ? (
                      <input
                        style={baselineColumnStyle}
                        className={tableStyles.awardInput}
                        type="text"
                        id={`baseline-${baselinePayloadId}`}
                        defaultValue={value}
                        onChange={(e) => handleBaselinePayload && handleBaselinePayload(e, baselinePayloadId, rowIndex, ucid)}
                        onBlur={(e) => handleFormatBaseline && handleFormatBaseline(e, rowIndex, columnIndex, baselinePayloadId, ucid)}
                        onPaste={(e) => handleBaselinePaste && handleBaselinePaste(e, rowIndex, columnIndex, ucid)}
                      />
                    ) : (
                      <span className={tableStyles.cellContent}>{value}</span>
                    )
                  }
                </span>
              </div>
            </div>
          );
        }

        if (
          isTrpRow
          && (
            ucid === 'baseline_price'
            || ucid === 'extended_price'
            || ucid === 'tranformed_price'
            || ucid === bestBidColumnId
          )
        ) {
          const derivedTransformationBaseline = getDerivedLotBaselineDisplay('transformation_price');
          const value = ucid === 'baseline_price'
            ? (formatPriceValue(transformationPrice?.baseline_price, 2) || derivedTransformationBaseline || '-')
            : (transformationPrice?.value || transformationPrice?.baseline_price || '-');
          const formattedValue = value === '-' ? value : formatPriceValue(value, 2);
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{ display: 'flex', justifyContent: parentColumn.justifyContent, height: '100%', overflow: 'hidden', fontWeight: 'bold' }}>
                  <span>{formattedValue !== '-' ? currencySymbol : ''}</span>
                  <span className={tableStyles.cellContent}>{formattedValue}</span>
                </span>
              </div>
            </div>
          );
        }
        if (isTrpRow && ucid === 'best_bid' && parentColumn.isBuyerFilled) {
          let displayValue = '';
          const bestFromAward = award?.event_saving_at_transformed_price?.total;
          if (bestFromAward !== undefined && bestFromAward !== null && bestFromAward !== '') {
            displayValue = formatPriceValue(bestFromAward, 2);
          } else if (bestBids && bestBids.get('lstLtpBestBids')) {
            displayValue = bestBids.get('lstLtpBestBids').get('transformation_price') || '';
          }
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{
                  display: 'flex',
                  justifyContent: parentColumn.justifyContent,
                  height: '100%',
                  overflow: 'hidden',
                  fontWeight: 'bold'
                }}
                >
                  <span>{displayValue !== '' ? currencySymbol : ''}</span>
                  <span className={tableStyles.cellContent}>{displayValue || '-'}</span>
                </span>
              </div>
            </div>
          );
        }
        if (isTrpRow && ucid === 'event_saving' && parentColumn.isBuyerFilled) {
          let displayValue = '';
          const rawSaving = award?.event_saving_at_transformed_price?.event_saving;
          if (rawSaving !== undefined && rawSaving !== null && rawSaving !== '') {
            displayValue = formatPriceValue(rawSaving, 2);
          } else {
            const baselineValue = parseNumericCell(transformationPrice?.baseline_price);
            let bestBidValue = NaN;
            if (bestBids && bestBids.get('lstLtpBestBids')) {
              const bestBidStr = bestBids.get('lstLtpBestBids').get('transformation_price') || '';
              bestBidValue = parseNumericCell(bestBidStr);
            }
            if (!Number.isNaN(baselineValue) && !Number.isNaN(bestBidValue)) {
              const eventSaving = computeEventSaving(baselineValue, bestBidValue);
              if (eventSaving !== 0) {
                displayValue = formatPriceValue(eventSaving, 2);
              }
            }
          }
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{ display: 'flex', justifyContent: parentColumn.justifyContent, height: '100%', overflow: 'hidden', fontWeight: 'bold' }}>
                  <span>{displayValue !== '' ? currencySymbol : ''}</span>
                  <span className={tableStyles.cellContent}>{displayValue || '-'}</span>
                </span>
              </div>
            </div>
          );
        }
        if (isTrpRow && ucid === 'award_savings' && parentColumn.isBuyerFilled) {
          const awardSavingRaw = award?.award_saving_at_transformed_price?.award_saving;
          const displayValue = (
            awardSavingRaw === undefined || awardSavingRaw === null || awardSavingRaw === ''
              ? ''
              : formatPriceValue(awardSavingRaw, 2)
          );
          return (
            <div key={key} style={style}>
              <div
                className={classNames(
                  tableStyles.row,
                  tableStyles.borderRight,
                  tableStyles.supplierValue,
                  tableStyles.padCell
                )}
                data-column={termName}
                data-rowid={rowIndex + 1}
              >
                <span style={{ display: 'flex', justifyContent: parentColumn.justifyContent, height: '100%', overflow: 'hidden', fontWeight: 'bold' }}>
                  <span>{displayValue !== '' ? currencySymbol : ''}</span>
                  <span className={tableStyles.cellContent}>{displayValue || '-'}</span>
                </span>
              </div>
            </div>
          );
        }

        return (
          <div key={key} style={style}>
            <div
              className={classNames(
                tableStyles.row,
                tableStyles.borderRight,
                tableStyles.supplierValue,
                tableStyles.padCell
              )}
              data-column={termName}
              data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}></span>
            </div>
          </div>
        );
      }
      
      // Default empty cell for unexpected rows
      return (
        <div
          key={key}
          style={style}
        >
          <div
            className={classNames(
              tableStyles.row,
              tableStyles.borderRight,
              tableStyles.supplierValue,
              tableStyles.padCell
            )}
            data-column={termName}
            data-rowid={rowIndex + 1}
            >
              <span className={tableStyles.cellContent}>
                {/* Default empty cell */}
              </span>
            </div>
        </div>
      );
    }
    
    function _cellRenderer({
      columnIndex,
      key,
      rowIndex: rowIndexOriginal,
      style
    }) {
      const width = columnWidth({ index: columnIndex });
      if (!width) {
        return null;
      }
      // Handle header row
      if (rowIndexOriginal === 0) {
        return (
          _headerRenderer({
            columnIndex,
            key,
            rowIndex: rowIndexOriginal - 1,
            style
          })
        )
      }
      
      const rowIndex = rowIndexOriginal - 1;
      
      // Check if this is a lot level field row (after line items)
      const isLotLevelRow = rowIndex >= lineItems.length;
      
      if (isLotLevelRow) {
        // Handle lot level fields, lot sub total, and lot total price rows
        const lotRow = renderLotLevelRow(rowIndex, columnIndex, key, style);
        if (isLotFullyGreyedOut && isAwardDraft && isValidElement(lotRow)) {
          return cloneElement(lotRow, {
            className: classNames(lotRow.props.className, 'excluded'),
          });
        }
        return lotRow;
      }
      
      const columnValue = getColumnValue(rowIndex, columnIndex);
      const isForSupplier = Array.isArray(columnValue);
      const parentColumn = getColumnInstance(rowIndex, columnIndex);
      const supplierName = parentColumn.safeAccess('supplier_name')
      const dataSupplier = `${
        parentColumn.isAlternateBid ? `${supplierName} (Alternate Bid)` : supplierName
      }`
      
      const row = lineItems[rowIndex];
      const count = responsesCountByRow.get(row.urid);
      if (
        showLineItemsWithNoResponse 
        && ( !(count === undefined || count === 0 ) || responseNeededForAllLineItems)
      ) {
        return null;
      }
      const EMPTY_ROW_BACKGROUND = 'rgba(255, 82, 0, 0.10)'
      const supplierId = parentColumn.safeAccess('supplier_id');
      const termName = parentColumn.safeAccess('term_name');
      const { urid: rowId } = row;

      const isExcluded = excludedSet.has(`${rowId}`);
      const isIgnored = isExcluded && ignoredSet.has(`${rowId}`);
      const isAwardDisabled = isExcluded || checkIfAwardIsBlocked(participatingSuppliersMap, supplierId, blockedConfig);
      
      const getLineAward = () => {
        if (isIgnored) {
          return '';
        }
        if (optimizedAt === 'lot') {
          return '-';
        }
        const { lineItemShares } = props;
        return (
            lineItemShares
            && lineItemShares[rowId]
            && lineItemShares[rowId][supplierId]
        ) || '';
      }

      const onLineAwardInput = (e) => {
        if (isNaN(e.target.value)) {
          return;
        }
        const parsedValue = parseInt(e.target.value, 10);
        const value = (
          awardBy === 'Qty'
          ? parsedValue
          : Math.min(parsedValue, 100)
        );
        setShares(shares => {
          const oldLineShares = shares.line_item_level_shares ? shares.line_item_level_shares[rowId] : {};
          let newLineShares = {
            ...oldLineShares,
          };
          if (isNaN(value)) {
            delete newLineShares[supplierId];
          }
          else {
            newLineShares[supplierId] = value;
          }
          return ({
            ...shares,
            line_item_level_shares: {
              ...shares.line_item_level_shares,
              [rowId]: newLineShares,
            }
          });
        });
      }

      const onLinePaste = (e) => {
        const pastedData = parseHTML(e);
        if (!(Array.isArray(pastedData) && (pastedData.length > 1 || pastedData[0].length > 1))) {
          return;
        }
        e.preventDefault();
        let index = rowIndex;
        setShares(shares => {
          const oldLineShares = shares.line_item_level_shares || {};
          pastedData.forEach(data => {
            if (index >= lineItems.length) {
              return;
            }
            const parsedValue = parseInt(data[0], 10);
            const value = (
              awardBy === 'Qty'
              ? parsedValue
              : Math.min(parsedValue, 100)
            );
            const currentRowId = lineItems[index].urid;
            const currentShares = oldLineShares[currentRowId] || {};
            currentShares[supplierId] = value;
            oldLineShares[currentRowId] = currentShares;
            index++;
          });
          return ({
            ...shares,
            line_item_level_shares: {
              ...oldLineShares
            }
          });
        });
      }
      
      const showError = isAwardDraft && optimizedAt === 'line' && lineErrorSet && lineErrorSet.has(rowId);
      const isEmpty = (count === undefined || count === 0) && !responseNeededForAllLineItems;
      const columnInstance = getColumnInstance(rowIndex, columnIndex)
      const isBaselineColumn = columnInstance.ucid === 'baseline_price'
      const isBaselineEditable = isBaselineColumn && isColumnEditable
      // =====================================================================
      // calculate background color of cell container
      // to show orange background when there are no
      // responses for the line-item
      // =====================================================================
      let containerBackgroundColor = ''
      if (isEmpty && !isBaselineColumn) {
        containerBackgroundColor = EMPTY_ROW_BACKGROUND
      }
      else if (isBaselineColumn) {
        if (showLineItemsWithNoResponse) {
          if (!isColumnEditable) {
            containerBackgroundColor = EMPTY_ROW_BACKGROUND
          }
        }
        else {
          if (isEmpty) {
            containerBackgroundColor = EMPTY_ROW_BACKGROUND
          }
        }
      }
      return (
        <div
          key={key}
          className={classNames({
            'error-cell': showError,
            'excluded': isExcluded && isAwardDraft,
            [tableStyles.editable]: isBaselineEditable
          })}
          
          style={{
            ...style,
            background: containerBackgroundColor
          }}
        >
          <div
            className={classNames(
              {
                [layoutStyles.flex]: isForSupplier,
                [tableStyles.supplierGroup]: isForSupplier,
              },
              {
                [tableStyles.padCell]: !isForSupplier,
                [tableStyles.awardCheckbox]: parentColumn.isAwardColumn
              },
              tableStyles.row,
              tableStyles.borderRight,
              tableStyles.supplierValue
            )}
            data-column={termName}
            data-rowid={rowIndexOriginal}
          >
          {
            Array.isArray(columnValue)
            ? columnValue.map(({
                value: originalValue,
                ...rest
            }, index) => {
              const value = !isIgnored ? originalValue : null;
              const { ucid } = rest;
              const column = new Column(rest);
              const transformationCondition = !column.isTransformation || isTransformationAppliedAtLineItemLevel;
              return (
                  <div
                    key={index}
                    className={classNames(
                      layoutStyles.flex,
                      layoutStyles.flex1,
                      !isIgnored && !column.isPTF && checkBestBid(rowIndex, columnIndex, ucid),
                      {
                          [tableStyles.padCell]: true,
                          [tableStyles.editable]: column.isEditable && !column.isLotOptimized && !isExcluded,
                          excluded: column.isEditable && isAwardDisabled
                      },
                      tableStyles.classicColumn
                    )}
                    data-column={column.termName}
                    data-supplier={dataSupplier}
                    data-rowid={rowIndexOriginal}
                  >
                  {
                      !column.isEditable ?
                      (
                          <span
                            style={{
                              display: column.isEditable ? 'none' : 'flex',
                              justifyContent: column.justifyContent,
                              width: '100%'
                            }}
                          >
                              <span>
                              {
                                  column.isPrice
                                  && transformationCondition
                                  && !showLineItemsWithNoResponse
                                  && value
                                  && currencySymbol
                              }
                              </span>
                              {
                                  column.type === 'Text' ? (
                                      <span
                                          className={tableStyles.cellContent}
                                          dangerouslySetInnerHTML={{
                                              __html: value
                                          }}
                                          title={window.htmlToFormattedText(value)}
                                      ></span>
                                  ) : (
                                      <span
                                          className={tableStyles.cellContent}
                                      >
                                      {
                                          transformationCondition
                                          && !showLineItemsWithNoResponse
                                          && value
                                      }
                                      </span>
                                  )
                              }
                              {
                              column.isPercentage
                              && transformationCondition
                              && (
                                  <span className={tableStyles.suffix}>
                                  %
                                  </span>
                              )
                              }
                          </span>
                      ) : (
                        <Fragment>
                          <input
                            className={tableStyles.awardInput}
                            value={getLineAward()}
                            data-supplier={supplierId}
                            onChange={onLineAwardInput}
                            onPaste={onLinePaste}
                            disabled={viewAwardScenario || isAwardDisabled}
                            style={awardColumnStyle}
                          />
                          {
                            awardBy === '%' && optimizedAt === 'line' && (
                              <span>%</span>
                            )
                          }
                        </Fragment>
                      )
                  }
                  </div>
              )
            }) : columnValue
          }
          </div>
        </div>
      );
    }
  
    useEffect(() => {
      if (isDummy) {
        measure();
      }
    }, [isDummy])
  
    const refProps = {}
    if (setContentRef) {
      refProps.ref = setContentRef
    }
  
    return (
      <MultiGrid
        cellRenderer={_cellRenderer}
        enableFixedColumnScroll
        enableFixedRowScroll
        height={height}
        rowHeight={rowHeight}
        style={STYLE}
        styleBottomLeftGrid={STYLE_BOTTOM_LEFT_GRID}
        styleTopLeftGrid={STYLE_TOP_LEFT_GRID}
        styleTopRightGrid={STYLE_TOP_RIGHT_GRID}
        hideTopRightGridScrollbar
        hideBottomLeftGridScrollbar
        columnCount={columnCount}
        columnWidth={columnWidth}
        fixedRowCount={1}
        {...props}
        {...refProps}
      />
    )
  }

export default InnerContent;
